import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  deviceInstancesData, getDevicesByPharmacy, timeAgo, healthStatusStyle
} from "@/data/adminInfraData";
import { getDeviceModelById } from "@/data/platformData";
import {
  Bell, Wifi, WifiOff, AlertTriangle, AlertOctagon, CheckCircle2, Activity, ChevronRight
} from "lucide-react";

// Compute the active alerts for a pharmacy (offline OR healthCheck warning/error)
export const getPharmacyDeviceAlerts = (pharmacyId) => {
  const devices = pharmacyId ? getDevicesByPharmacy(pharmacyId) : deviceInstancesData;
  return devices
    .map(d => {
      const m = getDeviceModelById(d.deviceModelId);
      const hcStatus = d.healthCheck.supportsHealthCheck ? d.healthCheck.status : "ok";
      let severity = null;
      if (!d.online) severity = "error";
      else if (hcStatus === "error") severity = "error";
      else if (hcStatus === "warning") severity = "warning";
      if (!severity) return null;
      return {
        id: d.id,
        modelName: m?.name || d.deviceModelId,
        modelType: m?.type || "—",
        serialNumber: d.serialNumber,
        lastSeen: d.lastSeen,
        online: d.online,
        severity,
        message: !d.online ? "Dispositivo offline" : (d.healthCheck.message || hcStatus)
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.severity === "error" ? 0 : 1) - (b.severity === "error" ? 0 : 1));
};

export const ManagerDeviceAlertsBell = ({ pharmacyId, dataTestId = "manager-bell" }) => {
  const navigate = useNavigate();
  const alerts = getPharmacyDeviceAlerts(pharmacyId);
  const errorCount = alerts.filter(a => a.severity === "error").length;
  const warnCount = alerts.filter(a => a.severity === "warning").length;
  const total = alerts.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid={dataTestId} aria-label="Notifiche dispositivi">
          <Bell className="h-5 w-5" />
          {total > 0 && (
            <span
              className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white ${
                errorCount > 0 ? "bg-rose-600" : "bg-amber-500"
              }`}
            >
              {total}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="end">
        <div className="p-3 border-b">
          <p className="font-semibold text-sm">Stato dispositivi</p>
          <p className="text-xs text-muted-foreground">
            {total === 0 ? "Tutti i dispositivi operativi" : (
              <span>
                {errorCount > 0 && <span className="text-rose-600 font-medium">{errorCount} errori</span>}
                {errorCount > 0 && warnCount > 0 && <span> · </span>}
                {warnCount > 0 && <span className="text-amber-600 font-medium">{warnCount} warning</span>}
              </span>
            )}
          </p>
        </div>
        <ScrollArea className="max-h-[60vh]">
          <div className="p-2 space-y-2">
            {total === 0 && (
              <div className="p-4 text-center text-sm text-muted-foreground">
                <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                Nessun alert attivo
              </div>
            )}
            {alerts.map(a => (
              <div
                key={a.id}
                className={`p-2.5 rounded-md border ${a.severity === "error" ? "border-rose-200 bg-rose-50/40" : "border-amber-200 bg-amber-50/40"}`}
                data-testid={`device-alert-${a.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{a.modelName}</p>
                    <p className="text-[11px] text-muted-foreground font-mono truncate">{a.serialNumber}</p>
                    <p className="text-xs mt-1 leading-tight">{a.message}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Badge
                      variant="outline"
                      className={a.severity === "error" ? "bg-rose-100 text-rose-700 border-rose-300 gap-0.5 text-[10px]" : "bg-amber-100 text-amber-700 border-amber-300 gap-0.5 text-[10px]"}
                    >
                      {a.severity === "error" ? <AlertOctagon className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                      {a.severity === "error" ? "Errore" : "Warning"}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">{timeAgo(a.lastSeen)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
        <div className="p-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between"
            onClick={() => navigate("/manager/monitoring")}
            data-testid="manager-bell-go-monitoring"
          >
            <span>Vedi monitoraggio completo</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Inline card for ManagerHomePage
export const ManagerDeviceAlertsCard = ({ pharmacyId }) => {
  const alerts = getPharmacyDeviceAlerts(pharmacyId);
  const allDevices = getDevicesByPharmacy(pharmacyId);
  const onlineCount = allDevices.filter(d => d.online).length;

  return (
    <div className="space-y-2" data-testid="manager-device-alerts-card">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Activity className="h-3.5 w-3.5 text-emerald-600" />
          {onlineCount}/{allDevices.length} online
        </span>
        {alerts.length === 0 && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Tutti operativi</span>}
      </div>

      {alerts.length === 0 && (
        <div className="p-4 rounded-lg border border-dashed text-center text-xs text-muted-foreground">
          Nessun alert dispositivo attivo
        </div>
      )}

      {alerts.map(a => (
        <div
          key={a.id}
          className={`p-3 rounded-lg border ${a.severity === "error" ? "border-rose-200 bg-rose-50/40" : "border-amber-200 bg-amber-50/40"}`}
          data-testid={`manager-alert-card-${a.id}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium text-sm truncate">{a.modelName}</p>
                <Badge variant="secondary" className="text-[10px]">{a.modelType}</Badge>
                {a.online ? (
                  <Badge variant="outline" className="bg-white text-emerald-700 border-emerald-200 gap-0.5 text-[10px]"><Wifi className="h-3 w-3" /> Online</Badge>
                ) : (
                  <Badge variant="outline" className="bg-rose-100 text-rose-700 border-rose-300 gap-0.5 text-[10px]"><WifiOff className="h-3 w-3" /> Offline</Badge>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                <span className="font-mono">{a.serialNumber}</span> · ultimo: {timeAgo(a.lastSeen)}
              </p>
              <p className="text-xs mt-1.5">{a.message}</p>
            </div>
            <Badge
              variant="outline"
              className={a.severity === "error" ? "bg-rose-100 text-rose-700 border-rose-300 gap-0.5 text-[10px] shrink-0" : "bg-amber-100 text-amber-700 border-amber-300 gap-0.5 text-[10px] shrink-0"}
            >
              {a.severity === "error" ? <AlertOctagon className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
              {a.severity === "error" ? "Errore" : "Warning"}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ManagerDeviceAlertsBell;
